import { useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useNavigate } from 'react-router';
import WidgetHeader from '@/components/common/WidgetHeader';
import { FooterButton } from '@/components/common/FooterButton';
import DateRange from '@/components/date/DateRange';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { dashboardMobileWidthValue, mobileWidth } from '@/constants';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { paths } from '@/constants/paths';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useControlledDates } from '@/hooks/useControlledDates';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import { useGetInvestorsOnboardingChartQuery } from '@/store/advisorInvestorsApi';
import {
  useGetClientLoginFrequencyQuery,
  useGetClientLoginRecencyQuery,
  useGetClientLoginTrendQuery,
} from '@/store/clientActivityApi';
import { ContentContainer } from '../contentContainer';
import ClientActivitySection from './clientActivity/ClientActivitySection';
import ClientLoginsTrendChart from './clientActivity/ClientLoginsTrendChart';

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

const { startDate: initialStartDate, endDate: initialEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

/**
 * Minimum height for anything tappable on a touch screen, per the Material
 * guideline the ticket cites (AC 2.1). Applied on mobile only: the same control
 * at its default height is fine under a mouse.
 */
const TOUCH_TARGET_MIN_HEIGHT = 48;

/**
 * Whether a query has anything worth waiting for.
 *
 * A query that has never run reports `isLoading: false`, and a refetch after a
 * date change sets only `isFetching`, so neither flag alone can be read as done.
 * Treating all three as busy keeps a skipped query from rendering as an empty
 * client book while authentication is still loading, and keeps the widget from
 * reporting itself loaded before any data has arrived.
 */
const isBusy = (query: {
  isLoading: boolean;
  isFetching: boolean;
  isUninitialized: boolean;
}) => query.isLoading || query.isFetching || query.isUninitialized;

/**
 * Trailing 7 and 30 day ranges are hidden: the login chart plots one point per
 * month, so either would draw a line through a single point.
 */
const isUsableRange = (filter: DataRangeFilterEnum) =>
  filter !== DataRangeFilterEnum.Trailing7Days &&
  filter !== DataRangeFilterEnum.Trailing30Days;

/**
 * Client Activity: one dashboard card summarising how the advisor's clients are
 * engaging with the portal.
 *
 * Two of the four sections answer "as of today" and are deliberately wired to
 * queries that take no date range, so moving the date selector leaves them
 * untouched (AC 3.2, AC 3.6).
 *
 * Each section is told it is loading whenever its query is busy, not only on
 * first load: RTK Query keeps the previous result during a refetch, so a date
 * change would otherwise leave the old numbers sitting under the new dates, and
 * a query still skipped while authentication loads would read as an empty book.
 */
export default function ClientActivityWidget({
  title,
  isWidget = false,
  isHalfWidth = false,
  onDataLoaded,
  container = 'widget',
  startDate: propStartDate,
  endDate: propEndDate,
}: StandardWidgetProps = {}) {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const authLoaded = useAuthIsLoaded();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const canViewClientActivity = useCanNavigateTo(
    paths.advisor.clients.activity,
  );

  const {
    ref: widgetWidthRef,
    isBelow: isWidgetNarrow,
    widthPx,
  } = useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);

  /**
   * The half-width flag says which slot the dashboard handed out, but the
   * measured width is what the content actually has: a full-width widget can
   * still be squeezed under the threshold by a sidebar or an embeddable host.
   */
  const stackSections = isMobileMatch || isHalfWidth || isWidgetNarrow;

  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate,
    propEndDate,
    initialWidgetStartDate: initialStartDate,
    initialWidgetEndDate: initialEndDate,
    isWidget,
  });

  const rangeArgs =
    authLoaded && startDate && endDate ? { startDate, endDate } : skipToken;

  const trend = useGetClientLoginTrendQuery(rangeArgs);
  const frequency = useGetClientLoginFrequencyQuery(rangeArgs);
  const onboarding = useGetInvestorsOnboardingChartQuery(
    authLoaded ? undefined : skipToken,
  );
  const recency = useGetClientLoginRecencyQuery(
    authLoaded ? undefined : skipToken,
  );

  const queries = [trend, frequency, onboarding, recency];
  const hasSettled = queries.every((query) => !isBusy(query));

  /**
   * WidgetLoader rebuilds `onDataLoaded` on every render and the callback moves
   * host state, so firing on its identity would report the same request over and
   * over and drive the host round again. Report each range once.
   */
  const reportedFor = useRef<string | null>(null);
  useEffect(() => {
    const key = `${startDate ?? ''}|${endDate ?? ''}`;
    if (!hasSettled || reportedFor.current === key) return;
    reportedFor.current = key;
    onDataLoaded?.();
  }, [hasSettled, startDate, endDate, onDataLoaded]);

  const handleDateChange = (nextStart: string, nextEnd: string) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const asOfToday = t('clientActivity.asOfToday', 'As of Today');
  const engagementSubtitle = t(
    'clientActivity.engagementSubtitle',
    'Client Logins between {{startDate}} and {{endDate}}',
    { startDate: startDate ?? '', endDate: endDate ?? '' },
  );

  return (
    <ContentContainer
      container={container}
      ref={widgetWidthRef}
      sx={{ minWidth: { xs: '100%', sm: 350 } }}
      hasCustomPadding
    >
      <Box sx={{ p: 3 }}>
        <WidgetHeader
          title={title ?? t('widgetClientActivity', 'Client Activity')}
          rightContent={
            <Box
              sx={
                isMobileMatch
                  ? {
                      '& .MuiPickersInputBase-root': {
                        minHeight: TOUCH_TARGET_MIN_HEIGHT,
                      },
                      '& .MuiButtonBase-root': {
                        minHeight: TOUCH_TARGET_MIN_HEIGHT,
                      },
                    }
                  : undefined
              }
            >
              <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
                <DateRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  shortcutsFilter={isUsableRange}
                  useWidgetNarrowIconField={isWidget && isWidgetNarrow}
                />
              </Box>
            </Box>
          }
        />

        <Box sx={{ mt: 3 }}>
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 600 }}
          >
            {t('clientActivity.totalLogins', 'Total Client Logins')}
          </Typography>
          <ClientLoginsTrendChart
            series={trend.data?.Records}
            isLoading={isBusy(trend)}
            isError={trend.isError}
            widthPx={widthPx}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: stackSections ? 'column' : 'row',
            gap: stackSections ? 4 : 3,
            mt: 4,
          }}
        >
          <ClientActivitySection
            title={t(
              'clientActivity.onboardingStatus',
              'Client Onboarding Status',
            )}
            subtitle={asOfToday}
            points={onboarding.data?.Data}
            isLoading={isBusy(onboarding)}
            isError={onboarding.isError}
            valueMode='count'
            stacked={stackSections}
            bucketKind='onboarding'
          />
          <ClientActivitySection
            title={t('clientActivity.engagement', 'Client Engagement')}
            subtitle={engagementSubtitle}
            points={frequency.data?.Data}
            isLoading={isBusy(frequency)}
            isError={frequency.isError}
            valueMode='percent'
            stacked={stackSections}
            bucketKind='frequency'
          />
          <ClientActivitySection
            title={t('clientActivity.pulse', 'Client Pulse')}
            subtitle={asOfToday}
            points={recency.data?.Data}
            isLoading={isBusy(recency)}
            isError={recency.isError}
            valueMode='percent'
            stacked={stackSections}
            bucketKind='recency'
          />
        </Box>
      </Box>

      {isWidget && !isEmbeddableBundle && canViewClientActivity && (
        <Box
          sx={
            isMobileMatch
              ? { '& .MuiButton-root': { minHeight: TOUCH_TARGET_MIN_HEIGHT } }
              : undefined
          }
        >
          <FooterButton
            buttonText={t('clientActivity.viewMore', 'View More')}
            onClick={() => {
              void navigate(paths.advisor.clients.activity);
            }}
          />
        </Box>
      )}
    </ContentContainer>
  );
}
